import React, { useState, useEffect } from 'react';
import { Box, Switch, FormControlLabel, Select, MenuItem } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { HomePage } from 'app/layout/pages/HomePage';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectUser } from 'oidc/user.selectors';
import { RetentionCharts } from './RetentionCharts';
import { QuickviewPanel } from './QuickviewPanel';
import { CandidatesGrid } from './CandidatesGrid';
import { CandidatesChips } from './CandidatesChips';
import { CandidatesFilter, defaultValues } from './WorkdeskFilter';
import { myCandidatesReducer, myCandidatesSliceKey, myCandidatesActions, defaultGridFilters } from './MyCandidates.redux';
import { myCandidatesSaga } from './MyCandidates.saga';
import { getFiscalPeriodDateRange, getCurrentFiscalYearStart } from 'utils/dates/dateExtraction';
import {
  selectMyCandidatesPlacements,
  selectMyCandidatesRecruiterOptions,
  selectMyCandidatesRecruiterId,
  selectMyCandidatesDataLoaded,
  selectMyCandidatesSelectedBarChartMonth,
} from './MyCandidates.selector';

export const MyCandidatesDesk = () => {
  const dispatch = useDispatch();
  const employeeId = useSelector(selectUser)?.userInfo?.employeeId ?? 0;
  const formMethods = useForm({ defaultValues, shouldUnregister: false });
  const { handleSubmit, setValue } = formMethods;
  const placements = useSelector(selectMyCandidatesPlacements);
  const recruiterOptions = useSelector(selectMyCandidatesRecruiterOptions);
  const dataLoaded = useSelector(selectMyCandidatesDataLoaded);
  const selectedBarChartMonth = useSelector(selectMyCandidatesSelectedBarChartMonth);
  const [visualsOn, setVisualsOn] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => getCurrentFiscalYearStart());
  const [monthsRange, setMonthsRange] = useState('12');

  const yearOptions = React.useMemo(() => {
    const current = getCurrentFiscalYearStart();
    return Array.from({ length: 11 }, (_, i) => current - i);
  }, []);

  useInjectReducer({ key: myCandidatesSliceKey, reducer: myCandidatesReducer });
  useInjectSaga({ key: myCandidatesSliceKey, saga: myCandidatesSaga });

  useEffect(() => {
    dispatch(myCandidatesActions.setSelectedBarChartMonth(null));
    dispatch(myCandidatesActions.setRecruiterId(employeeId));
    dispatch(myCandidatesActions.setGridFilters(defaultGridFilters));
    dispatch(myCandidatesActions.setSelectedQuickviewKey(null));
    setSelectedYear(getCurrentFiscalYearStart());
    setMonthsRange('12');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (employeeId) {
      dispatch(myCandidatesActions.fetchRecruiterOptionsAction({ userId: employeeId }));
    }
  }, [dispatch, employeeId]);

  const recruiterId = useSelector(selectMyCandidatesRecruiterId);

  useEffect(() => {
    const recruiter = recruiterId ?? employeeId;
    if (recruiter) {
      const { startDate, endDate } = getFiscalPeriodDateRange(
        monthsRange as '12' | 'Q1' | 'Q2' | 'Q3' | 'Q4',
        selectedYear,
      );
      dispatch(
        myCandidatesActions.fetchRecruitmentRetentionAction({
          recruiterId: recruiter,
          startDate: startDate.slice(0, 10),
          endDate: endDate.slice(0, 10),
        }),
      );
      if (!recruiterId) {
        dispatch(myCandidatesActions.setRecruiterId(employeeId));
      }
    }
  }, [dispatch, employeeId, monthsRange, selectedYear, recruiterId]);

  useEffect(() => {
    if (employeeId && recruiterOptions.length > 0) {
      const currentUser = recruiterOptions.find((r: { id: number }) => r.id === employeeId);
      if (currentUser) {
        setValue('placementRecruiter', currentUser);
      }
    }
  }, [employeeId, recruiterOptions.length, setValue]);

  useEffect(() => {
    if (dataLoaded && placements.length === 0) {
      setValue('discipline', []);
      setValue('specialty', []);
      setValue('states', '');
      setValue('candidateStatus', []);
      setValue('rebooked', 'all');
      setValue('bookedOutOfRange', 'all');
      setValue('placementStatus', []);
    }
  }, [dataLoaded, placements.length, setValue]);

  const toFilterValue = (v: unknown): string => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object' && v !== null && 'value' in v) return String((v as any).value ?? '');
    return String(v);
  };

  const toFilterArray = (v: unknown): { id: number; name: string; value: string }[] => {
    if (v == null) return [];
    if (!Array.isArray(v)) return [];
    return v
      .filter(item => item && typeof item === 'object' && (item as any).value !== 'All')
      .map((item: any) => ({ id: item.id ?? item.value, name: item.name ?? '', value: item.value ?? '' }));
  };

  const onApplyFilter = (values: Record<string, unknown>) => {
    const recruiter = values.placementRecruiter as { id?: number; name?: string } | null;
    const newRecruiterId = recruiter?.id ?? null;

    dispatch(
      myCandidatesActions.setGridFilters({
        discipline: toFilterArray(values.discipline),
        specialty: toFilterArray(values.specialty),
        states: toFilterValue(values.states) || 'all',
        candidateStatus: toFilterArray(values.candidateStatus),
        rebooked: toFilterValue(values.rebooked) || '',
        bookedOutOfRange: toFilterValue(values.bookedOutOfRange) || '',
        placementStatus: values.placementStatus ?? [],
      }),
    );

    if (newRecruiterId !== recruiterId) {
      dispatch(myCandidatesActions.setSelectedBarChartMonth(null));
      dispatch(myCandidatesActions.setRecruiterId(newRecruiterId));
      if (newRecruiterId) {
        const { startDate, endDate } = getFiscalPeriodDateRange(
          monthsRange as '12' | 'Q1' | 'Q2' | 'Q3' | 'Q4',
          selectedYear,
        );
        dispatch(
          myCandidatesActions.fetchRecruitmentRetentionAction({
            recruiterId: newRecruiterId,
            startDate: startDate.slice(0, 10),
            endDate: endDate.slice(0, 10),
          }),
        );
      }
    }
  };

  const sidePanelItems = [
    { title: 'Quickview', component: <QuickviewPanel /> },
    { title: 'Advanced Filters', component: <div style={{ minWidth: 236 }} /> },
    { title: 'Tiles', component: <div style={{ minWidth: 236 }} /> },
  ];

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onApplyFilter)}>
        <HomePage
          title="My Candidates - Retention"
          titleActions={
            <Box display="flex" alignItems="center" gap={2}>
              <FormControlLabel
                control={<Switch size="small" checked={visualsOn} onChange={e => setVisualsOn(e.target.checked)} />}
                label="Visuals"
              />
              <Select
                size="small"
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                sx={{ minWidth: 100 }}
              >
                {yearOptions.map(y => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
              <Select size="small" value={monthsRange} onChange={e => setMonthsRange(e.target.value)} sx={{ minWidth: 120 }}>
                <MenuItem value="12">12 Months</MenuItem>
                <MenuItem value="Q1">1st Quarter</MenuItem>
                <MenuItem value="Q2">2nd Quarter</MenuItem>
                <MenuItem value="Q3">3rd Quarter</MenuItem>
                <MenuItem value="Q4">4th Quarter</MenuItem>
              </Select>
            </Box>
          }
          stickIconArray={[3, 2, 1]}
          sidePanelItems={sidePanelItems}
          dataVisualization={
            <RetentionCharts
              items={placements}
              fyStartYear={selectedYear}
              visualsOn={visualsOn}
              selectedBarChartMonth={selectedBarChartMonth}
              onBarChartMonthClick={month => {
                dispatch(
                  myCandidatesActions.setSelectedBarChartMonth(
                    month === selectedBarChartMonth ? null : month,
                  ),
                );
              }}
            />
          }
          filters={
            <CandidatesFilter
              onApplyFilter={onApplyFilter}
              onAfterReset={() => {
                setSelectedYear(getCurrentFiscalYearStart());
                setMonthsRange('12');
              }}
            />
          }
          chips={<CandidatesChips onApplyFilter={onApplyFilter} />}
          table={<CandidatesGrid totalCandidates={placements.length} />}
        />
      </form>
    </FormProvider>
  );
};
